import * as React from 'react';
import { makeStyles, mergeClasses, shorthands, Subtitle2, Text, tokens } from '@fluentui/react-components';
import { defineMessage, useIntl } from 'react-intl';
import type {
  AccountLevel,
  BusinessModelEnum,
  ConceptStageEnum,
  FundingStageEnum,
  IndustryEnum,
} from '@foundershub/api-client';
import { businessModelLabels, conceptStageLabels, fundingStageLabels, industryLabels } from '../modules/intl/metadata';
import { accountLevelLabel, accountLevelToNumber } from '../modules/levels';

interface Props {
  fundingStage: Array<FundingStageEnum>;
  businessModel: Array<BusinessModelEnum>;
  conceptStage: Array<ConceptStageEnum>;
  industry?: Array<IndustryEnum>;
  level?: Array<AccountLevel>;
}

export function MetadataGrid({ fundingStage, businessModel, conceptStage, industry, level }: Props) {
  const classes = useStyles();
  const intl = useIntl();
  const listFormatter = new Intl.ListFormat(intl.locale, { style: 'short', type: 'unit' });
  return (
    <div className={classes.innerCards}>
      <div className={mergeClasses(classes.innerCardRow, classes.innerCardRowBorder)}>
        <div className={mergeClasses(classes.innerCard, classes.innerCardBorder)}>
          <Subtitle2>{intl.formatMessage(stageLabel)}</Subtitle2>
          <Text>
            {listFormatter.format(conceptStage.map((stage) => intl.formatMessage(conceptStageLabels[stage])))}
          </Text>
        </div>
        <div className={classes.innerCard}>
          <Subtitle2>{intl.formatMessage(fundingLabel)}</Subtitle2>
          <Text>{listFormatter.format(fundingStage.map((fs) => intl.formatMessage(fundingStageLabels[fs])))}</Text>
        </div>
      </div>

      <div className={classes.innerCardRow}>
        <div className={mergeClasses(classes.innerCard, classes.innerCardBorder)}>
          <Subtitle2>{intl.formatMessage(businessModelLabel)}</Subtitle2>
          {businessModel.length ? (
            <Text>{listFormatter.format(businessModel.map((bm) => intl.formatMessage(businessModelLabels[bm])))}</Text>
          ) : null}
        </div>
        {industry && (
          <div className={classes.innerCard}>
            <Subtitle2>{intl.formatMessage(industryLabel)}</Subtitle2>
            {industry.length ? (
              <Text>{listFormatter.format(industry.map((i) => intl.formatMessage(industryLabels[i])))}</Text>
            ) : null}
          </div>
        )}
        {level && (
          <div className={classes.innerCard}>
            <Subtitle2>{intl.formatMessage(levelLabel)}</Subtitle2>
            {level.length ? (
              <Text>
                {intl.formatList(
                  level.map((i) => intl.formatMessage(accountLevelLabel, { levelNumber: accountLevelToNumber[i] }))
                )}
              </Text>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  innerCards: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
  },
  innerCardRow: {
    display: 'flex',
    flexDirection: 'row',
    '@media screen and (max-width: 500px)': {
      display: 'block',
      flexWrap: 'wrap',
    },
  },
  innerCardRowBorder: {
    '@media screen and (min-width: 500px)': {
      ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    },
  },
  innerCard: {
    boxSizing: 'border-box',
    display: 'flex',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '50%',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXS),
    ...shorthands.padding(tokens.spacingHorizontalL),
    '@media screen and (max-width: 500px)': {
      ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    },
  },
  innerCardBorder: {
    ...shorthands.borderRight(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
  },
});

const stageLabel = defineMessage({
  defaultMessage: 'Development stage',
  description: 'stage label',
});
const fundingLabel = defineMessage({
  defaultMessage: 'Funding stage',
  description: 'label for funding stage',
});

const industryLabel = defineMessage({
  defaultMessage: 'Industry',
  description: 'label for industry the product markets to',
});

const businessModelLabel = defineMessage({
  defaultMessage: 'Business model',
  description: 'business model label',
});

const levelLabel = defineMessage({
  defaultMessage: 'Level',
  description: 'level label',
});
